import { useMemo } from 'react';
import { mapGridFilters, mapGridSorts, type GridQueryInput } from './gridUtils';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

type UseGridQueryInputParams = {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  fixedFilters?: GridQueryInput['filters'];
  search?: string;
};

type UseGridQueryInputResult = {
  queryInput: GridQueryInput;
  queryInputKey: string;
  fixedFiltersKey: string;
};

export const useGridQueryInput = ({
  paginationModel,
  sortModel,
  filterModel,
  fixedFilters,
  search,
}: UseGridQueryInputParams): UseGridQueryInputResult => {
  const fixedFiltersKey = useMemo(() => JSON.stringify(fixedFilters ?? []), [fixedFilters]);
  const quickFilterSearch = useMemo(() => {
    const quickValues = filterModel.quickFilterValues ?? [];
    const normalized = quickValues
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0);
    return normalized.length > 0 ? normalized.join(' ') : undefined;
  }, [filterModel.quickFilterValues]);
  const resolvedSearch = useMemo(() => {
    const trimmedSearch = search?.trim();
    if (trimmedSearch && trimmedSearch.length > 0) {
      return trimmedSearch;
    }
    return quickFilterSearch;
  }, [quickFilterSearch, search]);
  const queryInput = useMemo<GridQueryInput>(
    () => ({
      page: (paginationModel.page as number) + 1,
      pageSize: paginationModel.pageSize,
      sorts: mapGridSorts(sortModel),
      filters: [...mapGridFilters(filterModel), ...(fixedFilters ?? [])],
      search: resolvedSearch,
    }),
    [
      filterModel,
      fixedFilters,
      paginationModel.page,
      paginationModel.pageSize,
      resolvedSearch,
      sortModel,
    ],
  );
  const queryInputKey = useMemo(() => JSON.stringify(queryInput), [queryInput]);

  return { queryInput, queryInputKey, fixedFiltersKey };
};
